import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  AdminJobBoardsVacanciesGetListApplicantsResponseItems,
  AdminJobBoardsVacanciesGetListApplicantsResponseSummary,
  AdminsApi,
  Configuration,
  JobBoardsVacanciesApplicationStatusEnum,
  OrderDirectionEnum,
} from 'generated/api'
import { sortFieldJobApplicantsEnum } from './jobVacancyEnum'
import { axiosDefaultConfig } from 'constants/axios'
import Toast from 'components/toast/Toast'
import { IconChecklistCircle } from 'components/icons'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)

export const useJobVacancyApplicants = (shouldFetchOnMountFetchJobVacancyApplicants = true) => {
  const params = useParams()
  const { toast, toastContainer } = Toast()
  const [jobVacancyId, setJobVacancyId] = useState('')
  const [fetchStatusJobApplicants, setFetchStatusJobApplicants] = useState<
    'loading' | 'idle' | 'error'
  >('idle')
  const [summaryJobApplicants, setSummaryJobApplicants] =
    useState<AdminJobBoardsVacanciesGetListApplicantsResponseSummary>({
      statusAccepted: 0,
      statusPending: 0,
      statusRejected: 0,
      totalApplicant: 0,
    })
  const [jobVacancyApplicants, setJobApplicants] = useState<
    AdminJobBoardsVacanciesGetListApplicantsResponseItems[]
  >([])
  const [pageJobApplicants, setPageJobApplicants] = useState(1)
  const [limitJobApplicants, setLimitJobApplicants] = useState(10)
  const [totalPageJobApplicants, setTotalPageJobApplicants] = useState(1)
  const [sortFieldJobApplicants, setSortFieldJobApplicants] = useState<sortFieldJobApplicantsEnum>(
    sortFieldJobApplicantsEnum.DEFAULT,
  )
  const [sortDirectionJobApplicants, setSortDirectionJobApplicants] = useState<OrderDirectionEnum>(
    '' as OrderDirectionEnum,
  )
  const [selectedStatusJobApplicants, setSelectedStatusJobApplicants] =
    useState<JobBoardsVacanciesApplicationStatusEnum>('' as JobBoardsVacanciesApplicationStatusEnum)

  const [fetchStatusUpdateJobApplicant, setFetchStatusUpdateJobApplicant] = useState<
    'loading' | 'idle'
  >('idle')
  const [selectedApplicantId, setSelectedApplicantId] = useState('')
  const [isModalUpdateApplicantStatusVisible, setIsModalUpdateApplicantStatusVisible] =
    useState(false)
  const [nextApplicantStatus, setNextApplicantStatus] =
    useState<JobBoardsVacanciesApplicationStatusEnum>('' as JobBoardsVacanciesApplicationStatusEnum)

  useEffect(() => {
    if (params.jobId) {
      setJobVacancyId(params.jobId)
    }
  }, [params.jobId])

  useEffect(() => {
    if (jobVacancyId && shouldFetchOnMountFetchJobVacancyApplicants) fetchJobApplicants()
  }, [
    jobVacancyId,
    limitJobApplicants,
    pageJobApplicants,
    selectedStatusJobApplicants,
    sortDirectionJobApplicants,
    sortFieldJobApplicants,
  ])

  const fetchJobApplicants = async () => {
    try {
      setFetchStatusJobApplicants('loading')
      const response = await adminsApi.adminsJobBoardsVacanciesGetListApplicants(
        jobVacancyId,
        selectedStatusJobApplicants ? 'status' : undefined,
        selectedStatusJobApplicants ? selectedStatusJobApplicants : undefined,
        sortFieldJobApplicants ? sortFieldJobApplicants : undefined,
        sortDirectionJobApplicants ? sortDirectionJobApplicants : undefined,
        pageJobApplicants,
        limitJobApplicants,
      )
      setJobApplicants(response.data.items)
      setTotalPageJobApplicants(response.data.pagination.pages)
      setSummaryJobApplicants(response.data.summary)
      setFetchStatusJobApplicants('idle')
    } catch {
      setFetchStatusJobApplicants('error')
    }
  }

  const handleOnChangeLimitJobVacancyApplicants = (updatedLimit: number) => {
    setLimitJobApplicants(updatedLimit)
    setPageJobApplicants(1)
  }

  const handleOnChangePageJobVacancyApplicants = (updatedPage: number) => {
    setPageJobApplicants(updatedPage)
  }

  const handleOnChangeSelectedStatusApplicants = (
    updatedStatus: JobBoardsVacanciesApplicationStatusEnum,
  ) => {
    setSelectedStatusJobApplicants(updatedStatus)
    setPageJobApplicants(1)
  }

  const handleOnChangeSort = (updatedField: sortFieldJobApplicantsEnum) => {
    if (updatedField === sortFieldJobApplicants) {
      setSortDirectionJobApplicants(
        sortDirectionJobApplicants === OrderDirectionEnum.Asc
          ? OrderDirectionEnum.Desc
          : OrderDirectionEnum.Asc,
      )
    } else {
      setSortFieldJobApplicants(updatedField)
      setSortDirectionJobApplicants(OrderDirectionEnum.Desc)
    }
  }

  const handleOnOpenModalUpdateApplicantStatus = (
    id: string,
    nextStatus: JobBoardsVacanciesApplicationStatusEnum,
  ) => {
    setIsModalUpdateApplicantStatusVisible(true)
    setSelectedApplicantId(id)
    setNextApplicantStatus(nextStatus)
  }

  const handleOnCancelModalUpdateApplicantStatus = () => {
    setIsModalUpdateApplicantStatusVisible(false)
    setSelectedApplicantId('')
    setNextApplicantStatus('' as JobBoardsVacanciesApplicationStatusEnum)
  }

  const handleOnUpdateApplicantStatus = async () => {
    try {
      setFetchStatusUpdateJobApplicant('loading')
      await adminsApi.adminsJobBoardsVacanciesUpdateStatusApplicants(selectedApplicantId, {
        status: nextApplicantStatus,
      })
      setIsModalUpdateApplicantStatusVisible(false)
      setSelectedApplicantId('')
      setNextApplicantStatus('' as JobBoardsVacanciesApplicationStatusEnum)
      setFetchStatusUpdateJobApplicant('idle')
      await toast(
        'Anda berhasil mengubah status pelamar',
        'success',
        <IconChecklistCircle color="white" />,
      )
      pageJobApplicants === 1 ? await fetchJobApplicants() : setPageJobApplicants(1)
    } catch {
      await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      setFetchStatusUpdateJobApplicant('idle')
    }
  }

  return {
    toastContainer,
    fetchStatusJobApplicants,
    fetchStatusUpdateJobApplicant,
    summaryJobApplicants,
    jobVacancyApplicants,
    pageJobApplicants,
    limitJobApplicants,
    totalPageJobApplicants,
    sortFieldJobApplicants,
    sortDirectionJobApplicants,
    selectedStatusJobApplicants,
    isModalUpdateApplicantStatusVisible,
    nextApplicantStatus,
    fetchJobApplicants,
    setIsModalUpdateApplicantStatusVisible,
    setNextApplicantStatus,
    handleOnChangeLimitJobVacancyApplicants,
    handleOnChangePageJobVacancyApplicants,
    handleOnChangeSelectedStatusApplicants,
    handleOnChangeSort,
    handleOnOpenModalUpdateApplicantStatus,
    handleOnCancelModalUpdateApplicantStatus,
    handleOnUpdateApplicantStatus,
  }
}
